<template>
  <div class="home bg-slate-50/50 dark:bg-slate-950 min-h-screen transition-colors">
    <MenuComponent />
    
    <main class="pt-20">

      <!-- HERO SECTION -->
      <section class="relative min-h-[calc(100vh-80px)] flex items-center overflow-hidden py-16">
        <div class="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] bg-primary/10 rounded-full blur-[120px] -z-10 pointer-events-none"></div>
        <div class="absolute bottom-[10%] left-[-5%] w-[400px] h-[400px] bg-blue-400/10 rounded-full blur-[100px] -z-10 pointer-events-none"></div>
        
        <div class="max-w-[1440px] mx-auto px-8 w-full">
          <div class="grid lg:grid-cols-2 gap-16 items-center">
            
            <div class="max-w-2xl py-10">
              <!-- Etiqueta de estado -->
              <div class="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 px-4 py-1.5 rounded-full mb-8">
                <span class="relative flex h-2 w-2">
                  <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                  <span class="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                </span>
                <span class="text-primary text-xs font-bold uppercase tracking-widest">Desarrollo y Consultoría IT</span>
              </div>

              <h1 class="text-5xl lg:text-7xl font-black text-slate-900 dark:text-white leading-[1.05] tracking-[-0.04em] mb-8">
                Desarrollo de software <span class="text-primary">a la medida</span> para empresas
              </h1>

              <p class="text-lg lg:text-xl text-slate-600 dark:text-gray-400 font-normal leading-relaxed mb-10 max-w-xl">
                Creamos páginas web corporativas, plataformas personalizadas y brindamos consultoría IT para optimizar tus procesos y acelerar tus resultados.
              </p>

              <div class="flex flex-wrap gap-5">
                <RouterLink to="/contacto"
                  class="h-14 px-8 bg-primary text-white font-bold rounded-xl shadow-lg shadow-primary/25 hover:opacity-90 transition-all active:scale-95 flex items-center justify-center">
                  Comenzar proyecto
                </RouterLink>
                <RouterLink to="/servicios"
                  class="h-14 px-8 bg-white dark:bg-slate-900 border border-slate-300 dark:border-white/10 text-slate-900 dark:text-white font-bold rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-all flex items-center justify-center gap-2">
                  Explorar servicios
                  <span class="material-symbols-outlined text-xl">arrow_forward</span>
                </RouterLink>
              </div>

              <!-- Partners / Trust footer inside hero -->
              <div class="mt-16 pt-8 border-t border-slate-200 dark:border-white/10 flex items-center gap-6">
                <span class="text-xs font-bold uppercase tracking-widest text-slate-400">Tecnología para empresas</span>
                <div class="flex gap-4">
                  <div class="w-7 h-7 bg-slate-200 dark:bg-slate-800 rounded-md"></div>
                  <div class="w-7 h-7 bg-slate-200 dark:bg-slate-800 rounded-md"></div>
                  <div class="w-7 h-7 bg-slate-200 dark:bg-slate-800 rounded-md"></div>
                </div>
              </div>
            </div>

            <!-- Hero Graphic / Illustration (Desktop) -->
            <div class="relative hidden lg:block">
              <div class="relative z-10 w-full aspect-square max-w-[560px] ml-auto">
                <div class="absolute inset-0 bg-gradient-to-br from-primary/15 to-blue-600/10 rounded-[3rem] rotate-3 overflow-hidden border border-slate-200/80 dark:border-white/10 shadow-2xl backdrop-blur-3xl flex items-center justify-center">
                  <div class="relative w-64 h-64 rounded-full border border-primary/30 flex items-center justify-center">
                    <div class="w-44 h-44 rounded-full border border-primary/50 flex items-center justify-center">
                      <div class="w-28 h-28 rounded-full bg-gradient-to-tr from-primary to-blue-500 shadow-xl flex items-center justify-center text-white">
                        <span class="material-symbols-outlined text-5xl">monitoring</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      <!-- CLIENTES / TRUST SECTION -->
      <section class="relative overflow-hidden bg-slate-900 text-white py-20">
        <div class="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[100px] pointer-events-none"></div>
        <div class="absolute bottom-0 right-1/4 w-80 h-80 bg-blue-400/10 rounded-full blur-[80px] pointer-events-none"></div>

        <div class="relative z-10 max-w-[1440px] mx-auto px-8">
          <div class="text-center mb-14">
            <span class="inline-block text-xs font-bold uppercase tracking-[0.25em] text-primary mb-3">Nuestros clientes</span>
            <h2 class="text-3xl md:text-4xl font-extrabold tracking-tight mb-4">
              Empresas que confían en nosotros
            </h2>
            <p class="text-slate-400 max-w-xl mx-auto text-base">
              Trabajamos con organizaciones que buscan crecer con tecnología de calidad y soporte real.
            </p>
          </div>

          <div class="flex flex-wrap items-stretch justify-center gap-8 mb-14">
            <!-- Ingeniería del Agua -->
            <div class="flex flex-col items-center bg-white/5 border border-white/10 backdrop-blur-md rounded-3xl p-8 hover:bg-white/10 hover:border-primary/30 hover:-translate-y-2 transition-all duration-300 w-72">
              <div class="bg-white rounded-2xl p-5 mb-5 w-full flex items-center justify-center shadow-inner">
                <img src="@/assets/ingenieria.jpg" alt="Ingeniería y Desarrollo del Agua" class="h-24 w-auto object-contain" />
              </div>
              <p class="font-semibold text-center text-sm leading-snug">Ingeniería y Desarrollo del Agua, S.A. de C.V.</p>
              <span class="mt-2 text-xs text-slate-400 uppercase tracking-wider">Infraestructura hidráulica</span>
            </div>

            <!-- ZANI -->
            <div class="flex flex-col items-center bg-white/5 border border-white/10 backdrop-blur-md rounded-3xl p-8 hover:bg-white/10 hover:border-primary/30 hover:-translate-y-2 transition-all duration-300 w-72">
              <div class="bg-black rounded-2xl p-5 mb-5 w-full flex items-center justify-center border border-white/10">
                <img src="@/assets/zani.png" alt="ZANI" class="h-24 w-auto object-contain" />
              </div>
              <p class="font-semibold text-center text-sm leading-snug">ZANI</p>
              <span class="mt-2 text-xs text-slate-400 uppercase tracking-wider">Empresa cliente</span>
            </div>
          </div>

          <!-- Stats Grid -->
          <div class="grid grid-cols-3 gap-6 max-w-2xl mx-auto border-t border-white/10 pt-10">
            <div class="text-center">
              <p class="text-3xl font-black text-white">+10</p>
              <p class="text-slate-400 text-sm mt-1">Proyectos entregados</p>
            </div>
            <div class="text-center border-x border-white/10">
              <p class="text-3xl font-black text-white">100%</p>
              <p class="text-slate-400 text-sm mt-1">Clientes satisfechos</p>
            </div>
            <div class="text-center">
              <p class="text-3xl font-black text-white">2+</p>
              <p class="text-slate-400 text-sm mt-1">Años de experiencia</p>
            </div>
          </div>
        </div>
      </section>

      <!-- LO QUE HACEMOS / VALUE PROPOSITION -->
      <section class="py-24">
        <div class="max-w-7xl mx-auto px-6 lg:px-8">
          <div class="grid lg:grid-cols-12 gap-12 items-center">
            
            <div class="lg:col-span-4">
              <span class="inline-block text-xs font-bold uppercase tracking-[0.2em] text-primary mb-3">Enfoque integral</span>
              <h2 class="text-slate-900 dark:text-white text-3xl md:text-4xl font-black leading-tight mb-4">
                Lo que hacemos por tu negocio
              </h2>
              <p class="text-slate-600 dark:text-gray-400 text-base leading-relaxed mb-6">
                Elegimos la herramienta correcta para cada proyecto, sin encasillarte en una sola tecnología.
              </p>
              <div class="flex items-center gap-2 text-primary font-bold text-sm bg-primary/10 px-4 py-2 rounded-xl inline-flex">
                <span class="material-symbols-outlined text-lg">verified</span>
                <span>Enterprise Ready</span>
              </div>
            </div>

            <div class="lg:col-span-8">
              <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
                
                <div class="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex flex-col">
                  <div class="w-12 h-12 bg-blue-50 dark:bg-blue-900/30 text-blue-600 rounded-xl flex items-center justify-center mb-4">
                    <span class="material-symbols-outlined text-2xl">dns</span>
                  </div>
                  <h3 class="font-bold text-lg mb-2 text-slate-900 dark:text-white">Backend robusto</h3>
                  <p class="text-xs text-slate-600 dark:text-gray-400 leading-relaxed">Sistemas a la medida que escalan con tu operación.</p>
                </div>

                <div class="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex flex-col">
                  <div class="w-12 h-12 bg-purple-50 dark:bg-purple-900/30 text-purple-600 rounded-xl flex items-center justify-center mb-4">
                    <span class="material-symbols-outlined text-2xl">smart_toy</span>
                  </div>
                  <h3 class="font-bold text-lg mb-2 text-slate-900 dark:text-white">Automatización IA</h3>
                  <p class="text-xs text-slate-600 dark:text-gray-400 leading-relaxed">Procesos manuales convertidos en flujos inteligentes.</p>
                </div>

                <div class="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex flex-col">
                  <div class="w-12 h-12 bg-red-50 dark:bg-red-900/30 text-red-600 rounded-xl flex items-center justify-center mb-4">
                    <span class="material-symbols-outlined text-2xl">language</span>
                  </div>
                  <h3 class="font-bold text-lg mb-2 text-slate-900 dark:text-white">Web moderna</h3>
                  <p class="text-xs text-slate-600 dark:text-gray-400 leading-relaxed">Plataformas pensadas para tus usuarios reales.</p>
                </div>

                <div class="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex flex-col">
                  <div class="w-12 h-12 bg-orange-50 dark:bg-orange-900/30 text-orange-600 rounded-xl flex items-center justify-center mb-4">
                    <span class="material-symbols-outlined text-2xl">smartphone</span>
                  </div>
                  <h3 class="font-bold text-lg mb-2 text-slate-900 dark:text-white">Apps móviles</h3>
                  <p class="text-xs text-slate-600 dark:text-gray-400 leading-relaxed">Una sola app optimizada para iOS y Android.</p>
                </div>

                <div class="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex flex-col sm:col-span-2">
                  <div class="w-12 h-12 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 rounded-xl flex items-center justify-center mb-4">
                    <span class="material-symbols-outlined text-2xl">database</span>
                  </div>
                  <h3 class="font-bold text-lg mb-2 text-slate-900 dark:text-white">Administración de datos</h3>
                  <p class="text-xs text-slate-600 dark:text-gray-400 leading-relaxed">Información centralizada, confiable y disponible al instante.</p>
                </div>

              </div>
            </div>

          </div>
        </div>
      </section>

      <!-- SERVICIOS PRINCIPALES (CATÁLOGO) -->
      <section id="servicios" class="py-24 bg-slate-100/50 dark:bg-slate-900/40 border-y border-slate-200 dark:border-white/5">
        <div class="max-w-7xl mx-auto px-6 lg:px-8">
          
          <div class="text-center max-w-2xl mx-auto mb-16">
            <span class="inline-block text-xs font-bold uppercase tracking-[0.2em] text-primary mb-3">Nuestras soluciones</span>
            <h2 class="text-3xl md:text-4xl font-black text-slate-900 dark:text-white tracking-tight mb-4">
              Servicios especializados
            </h2>
            <p class="text-slate-600 dark:text-gray-400 text-base">
              Soluciones digitales de alto impacto diseñadas para escalar su negocio y optimizar la eficiencia operativa.
            </p>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            
            <div class="group bg-white dark:bg-slate-900 p-8 rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div class="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-primary text-primary group-hover:text-white transition-colors">
                <span class="material-symbols-outlined text-3xl">terminal</span>
              </div>
              <h3 class="text-xl font-bold mb-3 text-slate-900 dark:text-white">Desarrollo empresarial</h3>
              <p class="text-slate-600 dark:text-gray-400 text-sm leading-relaxed">
                Diseñamos y construimos software robusto y escalable adaptado a los flujos reales de tu operación.
              </p>
            </div>

            <div class="group bg-white dark:bg-slate-900 p-8 rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div class="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-primary text-primary group-hover:text-white transition-colors">
                <span class="material-symbols-outlined text-3xl">api</span>
              </div>
              <h3 class="text-xl font-bold mb-3 text-slate-900 dark:text-white">Integraciones & APIs</h3>
              <p class="text-slate-600 dark:text-gray-400 text-sm leading-relaxed">
                Conectamos tus herramientas para crear un ecosistema digital unificado con flujo de datos en tiempo real.
              </p>
            </div>

            <div class="group bg-white dark:bg-slate-900 p-8 rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div class="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-primary text-primary group-hover:text-white transition-colors">
                <span class="material-symbols-outlined text-3xl">language</span>
              </div>
              <h3 class="text-xl font-bold mb-3 text-slate-900 dark:text-white">Páginas corporativas</h3>
              <p class="text-slate-600 dark:text-gray-400 text-sm leading-relaxed">
                Creamos sitios modernos, optimizados para rendimiento, contenido y generación de oportunidades comerciales.
              </p>
            </div>

            <div class="group bg-white dark:bg-slate-900 p-8 rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div class="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-primary text-primary group-hover:text-white transition-colors">
                <span class="material-symbols-outlined text-3xl">rule_settings</span>
              </div>
              <h3 class="text-xl font-bold mb-3 text-slate-900 dark:text-white">Automatización de procesos</h3>
              <p class="text-slate-600 dark:text-gray-400 text-sm leading-relaxed">
                Implementamos flujos que reducen tareas manuales, tiempos de operación y errores repetitivos.
              </p>
            </div>

            <div class="group bg-white dark:bg-slate-900 p-8 rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div class="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-primary text-primary group-hover:text-white transition-colors">
                <span class="material-symbols-outlined text-3xl">support_agent</span>
              </div>
              <h3 class="text-xl font-bold mb-3 text-slate-900 dark:text-white">Soporte y mantenimiento</h3>
              <p class="text-slate-600 dark:text-gray-400 text-sm leading-relaxed">
                Damos seguimiento técnico continuo para mantener tus plataformas estables, actualizadas y seguras.
              </p>
            </div>

            <div class="group bg-white dark:bg-slate-900 p-8 rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div class="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-primary text-primary group-hover:text-white transition-colors">
                <span class="material-symbols-outlined text-3xl">smartphone</span>
              </div>
              <h3 class="text-xl font-bold mb-3 text-slate-900 dark:text-white">Desarrollo móvil</h3>
              <p class="text-slate-600 dark:text-gray-400 text-sm leading-relaxed">
                Creamos aplicaciones multiplataforma para iOS y Android diseñadas para escalar con tu negocio.
              </p>
            </div>

          </div>
        </div>
      </section>

      <!-- CTA FINAL -->
      <section class="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div class="p-12 md:p-16 bg-[#0F172A] rounded-3xl text-center text-white flex flex-col items-center relative overflow-hidden shadow-2xl">
          <div class="absolute -top-24 -left-24 w-72 h-72 bg-primary/20 rounded-full blur-3xl pointer-events-none"></div>
          
          <h2 class="text-3xl md:text-4xl font-black mb-4 tracking-tight relative z-10">¿Impulsamos tu próximo proyecto?</h2>
          <p class="text-slate-300 mb-8 max-w-xl text-base md:text-lg relative z-10">
            Estamos listos para transformar tus ideas en soluciones tecnológicas escalables.
          </p>
          <div class="flex flex-wrap justify-center gap-4 relative z-10">
            <RouterLink to="/contacto"
              class="bg-primary text-white px-8 py-4 rounded-xl font-bold shadow-lg shadow-primary/30 hover:opacity-90 transition-all">
              Solicitar asesoría
            </RouterLink>
            <RouterLink to="/servicios"
              class="bg-white/10 border border-white/20 text-white px-8 py-4 rounded-xl font-bold hover:bg-white/20 transition-all">
              Conocer servicios
            </RouterLink>
          </div>
        </div>
      </section>

    </main>
  </div>
</template>

<script setup>
import MenuComponent from '@/components/MenuComponent.vue';
</script>